import React, { useContext } from 'react'
import { View } from 'oio-react'
import PropTypes from 'prop-types'
import uiConstants from 'config/constants/ui'
import NoteListBlock from 'src/apps/Notebook/components/NoteListBlock'
import { useMe, useOrganization } from 'src/graphql/hooks'
import NotebookContext, { NotebookContextProvider } from 'src/libraries/contexts/Notebook'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import { TOCContextProvider } from 'src/libraries/contexts/TOC'
import Logo from '~/components/MainMenu/Logo'
import MobileMainMenu from '~/components/MainMenu/Mobile'
import NavVertical from '~/components/MainMenu/NavVertical'
import ActionBlock from './ActionBlock'
import TOCMenu from './TOCMenu'

// Sidebar Menu is organized into 2 columns
// ColumnA contains the MainMenu
// ColumnB contains the Notebook List Menu (although in the future, this may contain)
// any secondary navigation (ie. may be used by Groups with Sidebar Layout)
const columnAFullSize = {
   d: '270px',
   e: '320px',
   f: '330px'
}

const columnACondensed = {
   d: '220px',
   e: '230px',
   f: '270px'
}

const columnB = {
   d: '300px',
   e: '22vw',
   f: '350px'
}

const SidebarLayout = ({ children }) => {
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const { currentNotebook, setCurrentNotebook } = useContext(NotebookContext)
   const { isLoggedIn } = useMe()
   const { organization } = useOrganization()
   const isBleedingEdge = organization.gatherAppVersion === 6

   // Column A expands and contracts in size depending on if a notebook is active
   const columnA = currentNotebook ? columnACondensed : columnAFullSize

   return (
      <>
         {isLoggedIn && theme.tmpViewMode === 'toc' && <TOCMenu />}
         <View
            display="block[a-c] flex[d-f]"
            backgroundColor={theme.tmpMainMenuBackgroundColor}
            width="100%"
            height="100vh[d-f]">
            <View
               display="block[a-c] flex[d-f]"
               width="100%"
               height="100%"
               style={{ overflow: 'hidden' }}>
               <MobileMainMenu />
               <View
                  display={theme.tmpViewMode === 'toc'
                     ? 'none'
                     : 'none[a-c] block[d-f]'
                  }
                  flex="0 0 auto"
                  position="relative"
                  backgroundColor={theme.tmpMainMenuBackgroundColor}
                  height="100%"
                  width={currentNotebook
                     ? `calc(${columnA.d} + ${columnB.d})[d]
                        calc(${columnA.e} + ${columnB.e})[e]
                        calc(${columnA.f} + ${columnB.f})[f]`
                     : `${columnA.d}[d]
                        ${columnA.e}[e]
                        ${columnA.f}[f]`
                  }>
                  <View
                     position="absolute"
                     top="0px"
                     left="0px"
                     height="100%"
                     borderRight={`1px solid ${theme.tmpMainMenuBorderColor}`}
                     width={`${columnA.d}[d] ${columnA.e}[e] ${columnA.f}[f]`}
                     zIndex={uiConstants.zIndexes.toc + 1}>
                     <View
                        flex="0 0 auto"
                        width="100%"
                        height="calc(100% - 160px)"
                        paddingBottom="90px"
                        paddingLeft={theme.tmpSpacing}
                        scroll="on">
                        <View
                           position="relative"
                           top="-7px"
                           paddingVertical={theme.tmpSpacingTop}
                           transition="200ms"
                           height={theme.tmpHeadingBlockHeight}>
                           {isBleedingEdge
                              ? <View width="2px" height="32px" />
                              : <Logo />
                           }
                        </View>
                        <View paddingRight="10px">
                           <View borderTop={`1px solid ${theme.tmpMainMenuLineColor}`} />
                           <NavVertical
                              onAppButtonClick={() => setCurrentNotebook(null)}
                              onNotebookButtonClick={notebookId => setCurrentNotebook(notebookId)}
                           />
                        </View>
                     </View>
                     <View
                        display="flex"
                        alignItems="flex-end"
                        width="100%"
                        height="160px"
                        paddingLeft={theme.tmpSpacing}
                        paddingRight="10px">
                        <ActionBlock />
                     </View>
                  </View>
                  <View
                     position="absolute"
                     top="0px"
                     left="0px"
                     bottom="0px"
                     display="flex"
                     justifyContent="flex-end"
                     transition="200ms"
                     width={`
                        calc(${columnA.d} + ${columnB.d})[d]
                        calc(${columnA.e} + ${columnB.e})[e]
                        calc(${columnA.f} + ${columnB.f})[f]
                     `}
                     transform={currentNotebook
                        ? 'translate3d(0,0,0)'
                        : `translate3d(calc((${columnB.d}) * -1),0,0)[d]
                           translate3d(calc((${columnB.e}) * -1),0,0)[e]
                           translate3d(calc((${columnB.f}) * -1),0,0)[f]`
                     }
                     zIndex={uiConstants.zIndexes.toc}>
                     {currentNotebook?.slug && (
                        <View
                           flex="0 0 auto"
                           width={`${columnB.d}[d] ${columnB.e}[e] ${columnB.f}[f]`}
                           height="100%"
                           borderRight={`1px solid ${theme.tmpMainMenuBorderColor}`}>
                           <NoteListBlock notebookSlug={currentNotebook?.slug} />
                        </View>
                     )}
                  </View>
               </View>
               <View
                  position="relative"
                  flex="1 1 auto"
                  height={theme.tmpViewportHeight}
                  scroll="on"
                  zIndex="1">
                  {children}
               </View>
            </View>
         </View>
      </>
   )
}

SidebarLayout.propTypes = {
   children: PropTypes.node.isRequired
}

// TODO: Abolish this when we restructure TOC and notebook contexts
// PR #757
const ConditionalSidebarLayout = ({ children }) => {
   const { organization } = useOrganization()
   const isBleedingEdge = organization.gatherAppVersion === 6

   const ConditionalTOCContextProvider = isBleedingEdge
      ? TOCContextProvider
      : React.Fragment

   const ConditionalNotebookContextProvider = isBleedingEdge
      ? NotebookContextProvider
      : React.Fragment

   return (
      <ConditionalTOCContextProvider>
         <ConditionalNotebookContextProvider>
            <SidebarLayout>
               {children}
            </SidebarLayout>
         </ConditionalNotebookContextProvider>
      </ConditionalTOCContextProvider>
   )
}

ConditionalSidebarLayout.propTypes = {
   children: PropTypes.node.isRequired
}

export default ConditionalSidebarLayout
